import { Classes, Intent } from "@blueprintjs/core";
import { OrgRole } from "@hex/common";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";

import {
  Flex,
  HexButton,
  HexKeyCombo,
  HexLinkButton,
  HexMenu,
  HexMenuItem,
  HexPopover,
  HexTag,
  HexTooltip,
  Txt,
} from "../../hex-components";
import { useCurrentUser } from "../../hooks/me/useCurrentUser";
import { useCurrentUserIsRoleOrAbove } from "../../hooks/me/useCurrentUserIsRoleOrAbove";
import { useToggleState } from "../../hooks/useToggleState";
import { Routes } from "../../route/routes";
import { Theme } from "../../theme/common/theme";
import { ClientCounterEvent } from "../../util/clientCounterEvent";
import { HotKeys } from "../../util/hotkeys";
import { TestSelectors, dataTest } from "../../util/testSelectors";
import { trackCounterEvent } from "../../util/trackEvent";
import { useHexFlag } from "../../util/useHexFlags";
import { useThreadsEnabledForUser } from "../agent/ask-from-home/hooks/useThreadsEnabledForUser";
import { HexLogo, HexLogoProps } from "../app/HexLogo";
import { NotificationInboxButton } from "../app/NotificationInbox";
import { useGetUnreadNotificationCountQuery } from "../app/NotificationInbox.generated";
import { UnreadIndicator } from "../app/UnreadIndicator";
import { ErrorBoundary } from "../common/ErrorBoundary";
import { ScrolledArea } from "../common/ScrolledArea";
import { WorkspaceSearchButton } from "../common/WorkspaceSearchButton";
import {
  AddIcon,
  CollectionIcon,
  ComponentIcon,
  ConfigureIcon,
  ContextStudioIcon,
  DataSourcesIcon,
  ExploreIcon,
  HomeIcon,
  LibraryIcon,
  MenuIcon,
  MoreMenuIcon,
  ProjectIcon,
  RecentsIcon,
  ThreadIcon,
} from "../icons/CustomIcons";
import {
  SIDEBAR_PADDING,
  SIDEBAR_SPACER,
} from "../sidebar/sidebarSharedStyles";

import {
  FLOATING_SIDEBAR_BOTTOM_SPACING,
  FLOATING_SIDEBAR_TOP_SPACING,
  NAVIGATION_BAR_HEIGHT,
  POPOVER_SIDEBAR_WIDTH,
} from "./constants";
import {
  CreateRComponentMenuItem,
  CreateRProjectMenuItem,
  ImportComponentMenuItem,
  ImportProjectMenuItem,
} from "./create-items/CreateMenuItems";
import {
  useCreateCollectionButton,
  useCreateComponentButton,
  useCreateExplorationButton,
  useCreateProjectButton,
  useCreateThreadButton,
} from "./create-items/useCreateAssets";
import { CreateAssetDialogs } from "./CreateNewButtons";
import { SidebarSectionsLoadingProvider } from "./favorite-projects-and-collection-rows/SidebarSection";
import { FavoriteCollections } from "./FavoriteCollections";
import { FavoriteData } from "./FavoriteData";
import { FavoriteProjects } from "./FavoriteProjects";
import { QuickCreateButtons } from "./QuickCreateButtons";
import { RecentItems } from "./RecentItems";
import {
  ActionButtons,
  CreateActionButton,
  FavoritesWrapper,
  MenuCreateButton,
  MorePopoverMenu,
  SidebarEntryRow,
  SplitCreateButton,
  TooltipContent,
} from "./sidebarNavEntryStyles";
import { useHasLibraryStatuses } from "./useHasLibraryStatuses";
import { WorkspaceDropdownPicker } from "./WorkspaceDropdownPicker";

enum PopoverSidebarState {
  HOVERED = "hovered",
  CLOSED = "closed",
}

const PopoverSidebarContainer = styled.div<{
  $variant: PopoverSidebarContextType["state"];
}>`
  width: ${POPOVER_SIDEBAR_WIDTH}px;
  background-color: transparent;

  position: absolute;
  top: ${NAVIGATION_BAR_HEIGHT}px;
  padding-top: 10px;
  max-height: calc(100% - ${NAVIGATION_BAR_HEIGHT}px);
  height: fit-content;
  z-index: 20;
  left: 0;
  padding-left: 10px;
  transition:
    transform
      ${({ theme }) =>
        `${theme.animation.durationMedium} ${theme.animation.easing}`},
    opacity
      ${({ theme }) => `${theme.animation.duration} ${theme.animation.easing}`};

  ${({ $variant }) => css`
    ${$variant === "hovered"
      ? `
          opacity: 1;
          transform: translateX(0);
          pointer-events: all;

        `
      : `
          transform: translateX(-30px);

          opacity: 0;
          pointer-events: none;
        `}
  `}
`;

const SidebarInner = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.backgroundColor.DEFAULT};
  width: ${POPOVER_SIDEBAR_WIDTH}px;
  max-height: calc(
    100vh -
      ${NAVIGATION_BAR_HEIGHT +
      FLOATING_SIDEBAR_TOP_SPACING +
      FLOATING_SIDEBAR_BOTTOM_SPACING +
      30}px
  );
  box-shadow: ${({ theme }) => theme.boxShadow.POPOVER};
  border-radius: ${({ theme }) => theme.borderRadius};
`;

const SidebarStickySection = styled.div<{
  $hasTopBorder?: boolean;
}>`
  display: flex;
  flex-direction: column;

  border-top: ${({ $hasTopBorder, theme }) =>
    $hasTopBorder ? `1px solid ${theme.borderColor.MUTED}` : "none"};
`;

const SidebarScrollableSection = styled(ScrolledArea)<{
  $navSidebarCleanup?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({ $navSidebarCleanup }) =>
    $navSidebarCleanup
      ? css`
          overflow-y: auto;

          > * {
            padding-left: ${SIDEBAR_PADDING}px;
            padding-right: ${SIDEBAR_PADDING}px;
          }

          ${SidebarEntryRow} {
            border-radius: ${({ theme }) => theme.borderRadius};

            &:hover {
              background-color: ${({ theme }) => theme.hoverColor};
            }

            > a.${Classes.BUTTON}:hover {
              background-color: transparent !important;
            }
          }
        `
      : css`
          overflow-y: scroll;
          padding: 0 ${SIDEBAR_PADDING}px;
        `}
`;

const FullWidthChildFlex = styled(Flex)`
  > * {
    width: 100%;
  }

  li {
    list-style: none;
  }
`;

const NavItemsContainer = styled.div`
  padding: 0 ${SIDEBAR_PADDING}px 10px;
  width: 100%;

  > * {
    width: 100%;
    border-radius: ${({ theme }) => theme.borderRadius};
  }

  > a.${Classes.BUTTON}:hover {
    background-color: ${({ theme }) => theme.hoverColor} !important;
  }
`;

const Spacer = styled.div`
  padding: ${SIDEBAR_SPACER}px;
`;

const UnreadIndicatorWrapper = styled.div`
  position: absolute;
  right: 4px;
  top: 12px;
  pointer-events: none;
`;

const NavigationItem = React.memo(function NavigationItem({
  actionButton,
  counterEvent,
  icon,
  title,
  to,
}: {
  icon: React.ReactElement;
  title: React.ReactNode;
  to: string;
  counterEvent: ClientCounterEvent;
  actionButton?: {
    tooltip: string;
    hotkey?: string;
    onClick: (evt: React.MouseEvent<HTMLElement>) => void;
    disabled?: boolean;
    moreMenu?: React.ReactElement;
  };
}) {
  const onClick = useCallback(() => {
    trackCounterEvent(counterEvent);
  }, [counterEvent]);

  if (actionButton == null) {
    return (
      <HexLinkButton
        css="justify-content: flex-start; align-items: center;"
        icon={icon}
        minimal={true}
        subtle={true}
        to={to}
        onClick={onClick}
      >
        {title}
      </HexLinkButton>
    );
  }

  const tooltipContent = (
    <TooltipContent>
      <span>{actionButton.tooltip}</span>
      {actionButton.hotkey && <HexKeyCombo combo={actionButton.hotkey} />}
    </TooltipContent>
  );

  return (
    <SidebarEntryRow>
      <HexLinkButton
        css="justify-content: flex-start; align-items: center; flex: 1; min-width: 0;"
        icon={icon}
        minimal={true}
        subtle={true}
        to={to}
        onClick={onClick}
      >
        {title}
      </HexLinkButton>
      {!actionButton.disabled && (
        <ActionButtons onClick={(evt) => evt.stopPropagation()}>
          <HexTooltip
            content={tooltipContent}
            hoverOpenDelay={0}
            placement="right"
          >
            {actionButton.moreMenu ? (
              <SplitCreateButton
                extraSmall={true}
                icon={<AddIcon />}
                minimal={true}
                popoverContent={actionButton.moreMenu}
                onClick={actionButton.onClick}
              />
            ) : (
              <CreateActionButton
                extraSmall={true}
                icon={<AddIcon />}
                minimal={true}
                onClick={actionButton.onClick}
              />
            )}
          </HexTooltip>
        </ActionButtons>
      )}
    </SidebarEntryRow>
  );
});

type PopoverSidebarContextType = {
  state: PopoverSidebarState;
  toggleSidebarClickState: () => void;
};

const PopoverSidebarContext = React.createContext<PopoverSidebarContextType>({
  state: PopoverSidebarState.CLOSED,
  toggleSidebarClickState: () => {},
});

export const usePopoverSidebarContext = (): PopoverSidebarContextType => {
  return React.useContext(PopoverSidebarContext);
};

export const PopoverSidebarProvider = React.memo(
  function PopoverSidebarProvider({ children }: { children: React.ReactNode }) {
    const [
      sidebarClickState,
      _unused,
      { setFalse: closeSidebarClickState, toggle: toggleSidebarClickState },
    ] = useToggleState(false);
    // Implement a way to autoclose the sidebar when clicking outside of the sidebar / button subtrees
    const handleClickToClose = useCallback(
      (evt: MouseEvent) => {
        if (!(evt.target instanceof Element)) {
          return;
        }
        if (
          evt.target.closest(dataTest(TestSelectors.POPOVER_SIDEBAR)) ||
          evt.target.closest(dataTest(TestSelectors.POPOVER_SIDEBAR_BUTTON))
        ) {
          return;
        }
        closeSidebarClickState();
      },
      [closeSidebarClickState],
    );
    useEffect(() => {
      document.addEventListener("click", handleClickToClose);

      return () => {
        document.removeEventListener("click", handleClickToClose);
      };
    }, [handleClickToClose]);

    const state = useMemo(() => {
      return sidebarClickState
        ? PopoverSidebarState.HOVERED
        : PopoverSidebarState.CLOSED;
    }, [sidebarClickState]);

    const value = useMemo(
      () => ({
        state,
        toggleSidebarClickState,
      }),
      [toggleSidebarClickState, state],
    );

    return (
      <PopoverSidebarContext.Provider value={value}>
        {children}
      </PopoverSidebarContext.Provider>
    );
  },
);

const PopoverSidebarButton = React.memo(function PopoverSidebarButton() {
  const { toggleSidebarClickState } = usePopoverSidebarContext();
  const { data: unreadNotificationCountData } =
    useGetUnreadNotificationCountQuery();
  const hasUnreadNotifications =
    (unreadNotificationCountData?.me?.unreadNotificationCount ?? 0) > 0;

  return (
    <Flex align="center" css="margin: 0 2px 0 10px; position: relative;">
      {hasUnreadNotifications && (
        <UnreadIndicatorWrapper>
          <UnreadIndicator isRead={false} />
        </UnreadIndicatorWrapper>
      )}
      <HexButton
        data-test={TestSelectors.POPOVER_SIDEBAR_BUTTON}
        icon={<MenuIcon />}
        minimal={true}
        onClick={toggleSidebarClickState}
      />
    </Flex>
  );
});

export const PopoverButtonWithLogo = React.memo(function PopoverButtonWithLogo({
  appIconLoading,
  customAppIconUrl,
}: Pick<HexLogoProps, "appIconLoading" | "customAppIconUrl">) {
  const currentUser = useCurrentUser();
  return (
    <Flex align="stretch" css="height: 100%;">
      <PopoverSidebarButton />
      <HexTooltip
        content={`Go to ${currentUser?.org.displayName ?? "workspace"} home`}
        position="bottom"
      >
        <HexLogo
          appIconLoading={appIconLoading}
          customAppIconUrl={customAppIconUrl}
          logoPadding="small"
        />
      </HexTooltip>
    </Flex>
  );
});

export const PopoverSidebar = React.memo(function PopoverSidebar() {
  const { canViewThreadById } = useThreadsEnabledForUser();
  const { state } = usePopoverSidebarContext();

  const navSidebarCleanup = useHexFlag("nav-sidebar-cleanup");

  // 'Ask' uses the exploreFromScratch feature gate to determine if the user can create Explores.
  const newThreadsListPageEnabled = canViewThreadById;
  const currentUser = useCurrentUser();
  const canViewAgentStats = currentUser?.org?.canViewAgentStats ?? false;

  const isExplorerOrAbove = useCurrentUserIsRoleOrAbove(OrgRole.EXPLORER);

  // We are checking for Explorer+ OR canViewAgentStats
  // because support users have that capability but are technically Guest roles
  const showContextStudio = isExplorerOrAbove || canViewAgentStats;

  const isEditorOrAbove = useCurrentUserIsRoleOrAbove(OrgRole.EDITOR);
  const isManagerOrAbove = useCurrentUserIsRoleOrAbove(OrgRole.MANAGER);
  const showAgentChatThreadViewerTab = process.env.NODE_ENV !== "production";
  const hasLibraryStatuses = useHasLibraryStatuses();
  const navigate = useNavigate();

  const { createNewProject, newProjectDisabled } = useCreateProjectButton({
    location: "popover_sidebar",
  });
  const { canCreateNewThread, createNewThread } = useCreateThreadButton({
    location: "popover_sidebar",
  });
  const { newCollectionDisabled, openNewCollectionDialog } =
    useCreateCollectionButton({ location: "popover_sidebar" });
  const { createPythonComponent, newComponentDisabled } =
    useCreateComponentButton({ location: "popover_sidebar" });
  const { createNewExplore, newExplorationDisabled } =
    useCreateExplorationButton({ location: "popover_sidebar" });

  const collectionActionButton = useMemo(
    () => ({
      tooltip: "New collection",
      hotkey: HotKeys.NEW_COLLECTION,
      onClick: openNewCollectionDialog,
      disabled: newCollectionDisabled,
    }),
    [openNewCollectionDialog, newCollectionDisabled],
  );

  const agentTasksEnabled = useHexFlag("agent-tasks");
  const allowRProjectCreation = useHexFlag("allow-r-project-creation");

  const projectMoreMenu = useMemo(
    () => (
      <HexMenu>
        <ImportProjectMenuItem location="popover_sidebar" />
        {allowRProjectCreation && (
          <CreateRProjectMenuItem location="popover_sidebar" />
        )}
      </HexMenu>
    ),
    [allowRProjectCreation],
  );

  const projectActionButton = useMemo(
    () => ({
      tooltip: "New project",
      hotkey: HotKeys.NEW_PROJECT,
      onClick: createNewProject,
      disabled: newProjectDisabled,
      moreMenu: projectMoreMenu,
    }),
    [createNewProject, newProjectDisabled, projectMoreMenu],
  );

  const componentMoreMenu = useMemo(
    () => (
      <HexMenu>
        <ImportComponentMenuItem location="popover_sidebar" />
        {allowRProjectCreation && (
          <CreateRComponentMenuItem location="popover_sidebar" />
        )}
      </HexMenu>
    ),
    [allowRProjectCreation],
  );

  const componentActionButton = useMemo(
    () => ({
      tooltip: "New component",
      hotkey: HotKeys.NEW_COMPONENT,
      onClick: createPythonComponent,
      disabled: newComponentDisabled,
      moreMenu: componentMoreMenu,
    }),
    [createPythonComponent, newComponentDisabled, componentMoreMenu],
  );

  const threadActionButton = useMemo(
    () => ({
      tooltip: "New thread",
      onClick: createNewThread,
      disabled: !canCreateNewThread,
    }),
    [createNewThread, canCreateNewThread],
  );

  return state === "closed" ? null : (
    <PopoverSidebarContainer
      $variant={state}
      data-test={TestSelectors.POPOVER_SIDEBAR}
    >
      <CreateAssetDialogs />

      <SidebarInner>
        <SidebarStickySection>
          <Flex css="margin: 12px 20px 4px 8px;" justify="between">
            <div css="margin-left: 8px;">
              <WorkspaceDropdownPicker
                css="max-width: 220px;"
                hideSettings={true}
                location="popover_sidebar"
                showUserPreferences={true}
              />
            </div>
            <Flex gap={8}>
              <NotificationInboxButton />
            </Flex>
          </Flex>
          <FullWidthChildFlex direction="column">
            <div //Create new button
              css={`
                padding: 4px 20px 10px;
              `}
            >
              <QuickCreateButtons location="popover_sidebar" />
            </div>

            <div //Search bar
              css={`
                padding: 0 ${SIDEBAR_PADDING}px;
              `}
            >
              <WorkspaceSearchButton
                isNavItem={true}
                location="popover_sidebar"
                minimal={true}
                textOverride="Search"
              />
            </div>
            {navSidebarCleanup ? (
              <NavItemsContainer>
                <NavigationItem
                  counterEvent="popover_sidebar.home.click"
                  icon={<HomeIcon />}
                  title="Home"
                  to={Routes.HOME.getUrl({})}
                />
                {showContextStudio && isManagerOrAbove && (
                  <NavigationItem
                    counterEvent="popover_sidebar.context_studio.click"
                    icon={<ContextStudioIcon />}
                    title="Context Studio"
                    to={Routes.CONTEXT_STUDIO.getUrl({})}
                  />
                )}
              </NavItemsContainer>
            ) : (
              <div
                css={`
                  padding: 0 ${SIDEBAR_PADDING}px 10px;
                  width: 100%;

                  > * {
                    width: 100%;
                  }
                `}
              >
                <NavigationItem
                  counterEvent="popover_sidebar.home.click"
                  icon={<HomeIcon />}
                  title="Home"
                  to={Routes.HOME.getUrl({})}
                />
                {showContextStudio && isManagerOrAbove && (
                  <NavigationItem
                    counterEvent="popover_sidebar.context_studio.click"
                    icon={<ContextStudioIcon />}
                    title="Context Studio"
                    to={Routes.CONTEXT_STUDIO.getUrl({})}
                  />
                )}
              </div>
            )}
          </FullWidthChildFlex>
        </SidebarStickySection>
        <SidebarScrollableSection $navSidebarCleanup={navSidebarCleanup}>
          <FullWidthChildFlex direction="column">
            {navSidebarCleanup ? (
              <>
                {agentTasksEnabled && (
                  <NavigationItem
                    counterEvent="popover_sidebar.tasks.click"
                    icon={<RecentsIcon />}
                    title="Tasks"
                    to={Routes.HOME.getUrl({ subView: "tasks" })}
                  />
                )}
                <NavigationItem
                  actionButton={collectionActionButton}
                  counterEvent="popover_sidebar.collections.click"
                  icon={<CollectionIcon />}
                  title="Collections"
                  to={Routes.HOME.getUrl({ subView: "collections" })}
                />
                <NavigationItem
                  actionButton={projectActionButton}
                  counterEvent="popover_sidebar.projects.click"
                  icon={<ProjectIcon />}
                  title="Projects"
                  to={Routes.HOME.getUrl({ subView: "all" })}
                />
                {isEditorOrAbove && (
                  <NavigationItem
                    actionButton={componentActionButton}
                    counterEvent="popover_sidebar.components.click"
                    icon={<ComponentIcon />}
                    title="Components"
                    to={Routes.HOME.getUrl({ subView: "components" })}
                  />
                )}
                <NavigationItem
                  counterEvent="popover_sidebar.data.click"
                  icon={<DataSourcesIcon />}
                  title="Data"
                  to={Routes.HOME.getUrl({ subView: "data" })}
                />
                {!isEditorOrAbove && newThreadsListPageEnabled && (
                  <NavigationItem
                    actionButton={threadActionButton}
                    counterEvent="popover_sidebar.threads.click"
                    icon={<ThreadIcon />}
                    title={
                      <Flex align="center" gap={6}>
                        Threads
                        <HexTag intent={Intent.PRIMARY} small={true}>
                          Beta
                        </HexTag>
                      </Flex>
                    }
                    to={Routes.HOME.getUrl({ subView: "threads" })}
                  />
                )}
                <HexPopover
                  autoFocus={false}
                  content={
                    <MorePopoverMenu>
                      {!isEditorOrAbove && (
                        <HexMenuItem
                          icon={<ComponentIcon />}
                          labelElement={
                            !newComponentDisabled ? (
                              <HexTooltip
                                content="New component"
                                hoverOpenDelay={0}
                                placement="right"
                              >
                                <MenuCreateButton
                                  icon={<AddIcon />}
                                  minimal={true}
                                  small={true}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    createPythonComponent(evt);
                                  }}
                                />
                              </HexTooltip>
                            ) : undefined
                          }
                          text="Components"
                          onClick={() => {
                            trackCounterEvent(
                              "popover_sidebar.components.click",
                            );
                            navigate(
                              Routes.HOME.getUrl({ subView: "components" }),
                            );
                          }}
                        />
                      )}
                      {isEditorOrAbove && newThreadsListPageEnabled && (
                        <HexMenuItem
                          icon={<ThreadIcon />}
                          labelElement={
                            canCreateNewThread ? (
                              <HexTooltip
                                content="New thread"
                                hoverOpenDelay={0}
                                placement="right"
                              >
                                <MenuCreateButton
                                  icon={<AddIcon />}
                                  minimal={true}
                                  small={true}
                                  onClick={(evt) => {
                                    evt.stopPropagation();
                                    createNewThread(evt);
                                  }}
                                />
                              </HexTooltip>
                            ) : undefined
                          }
                          text={
                            <Flex align="center" gap={6}>
                              Threads
                              <HexTag intent={Intent.PRIMARY} small={true}>
                                Beta
                              </HexTag>
                            </Flex>
                          }
                          onClick={() => {
                            trackCounterEvent("popover_sidebar.threads.click");
                            navigate(
                              Routes.HOME.getUrl({ subView: "threads" }),
                            );
                          }}
                        />
                      )}
                      {hasLibraryStatuses && (
                        <HexMenuItem
                          icon={<LibraryIcon />}
                          text="Library"
                          onClick={() => {
                            trackCounterEvent("popover_sidebar.library.click");
                            navigate(
                              Routes.HOME.getUrl({ subView: "library" }),
                            );
                          }}
                        />
                      )}
                      <HexMenuItem
                        icon={<ExploreIcon />}
                        labelElement={
                          !newExplorationDisabled ? (
                            <HexTooltip
                              content="New exploration"
                              hoverOpenDelay={0}
                              placement="right"
                            >
                              <MenuCreateButton
                                icon={<AddIcon />}
                                minimal={true}
                                small={true}
                                onClick={(evt) => {
                                  evt.stopPropagation();
                                  createNewExplore(evt);
                                }}
                              />
                            </HexTooltip>
                          ) : undefined
                        }
                        text="Explorations"
                        onClick={() => {
                          trackCounterEvent("popover_sidebar.explore.click");
                          navigate(
                            Routes.HOME.getUrl({ subView: "explorations" }),
                          );
                        }}
                      />
                      {showContextStudio && !isManagerOrAbove && (
                        <HexMenuItem
                          icon={<ContextStudioIcon />}
                          text="Context Studio"
                          onClick={() => {
                            trackCounterEvent(
                              "popover_sidebar.context_studio.click",
                            );
                            navigate(Routes.CONTEXT_STUDIO.getUrl({}));
                          }}
                        />
                      )}
                      {showAgentChatThreadViewerTab && (
                        <HexMenuItem
                          icon={<ConfigureIcon />}
                          text="Agent Dev Tools"
                          onClick={() =>
                            navigate(
                              Routes.HOME.getUrl({
                                subView: "agent-dev-tools",
                              }),
                            )
                          }
                        />
                      )}
                    </MorePopoverMenu>
                  }
                  minimal={true}
                  placement="bottom-start"
                >
                  <StyledHexMenuItem
                    icon={<MoreMenuIcon />}
                    text={<Txt fontColor="muted">More</Txt>}
                  />
                </HexPopover>
              </>
            ) : (
              <PopoverSidebarV1NavItems
                newThreadsListPageEnabled={newThreadsListPageEnabled}
                showContextStudio={showContextStudio && !isManagerOrAbove}
              />
            )}
            <Spacer />
          </FullWidthChildFlex>

          <ErrorBoundary>
            {navSidebarCleanup ? (
              <FavoritesWrapper>
                <SidebarSectionsLoadingProvider>
                  <RecentItems hideIfEmpty={true} location="popover_sidebar" />
                  <FavoriteProjects
                    hideIfEmpty={true}
                    location="popover_sidebar"
                  />
                  <FavoriteCollections
                    hideIfEmpty={true}
                    location="popover_sidebar"
                  />
                  <FavoriteData hideIfEmpty={true} location="popover_sidebar" />
                </SidebarSectionsLoadingProvider>
              </FavoritesWrapper>
            ) : (
              <Flex direction="column" gap={12}>
                <RecentItems hideIfEmpty={true} location="popover_sidebar" />
                <FavoriteProjects
                  hideIfEmpty={true}
                  location="popover_sidebar"
                />
                <FavoriteCollections
                  hideIfEmpty={true}
                  location="popover_sidebar"
                />
                <FavoriteData hideIfEmpty={true} location="popover_sidebar" />
              </Flex>
            )}
          </ErrorBoundary>
          <Spacer />
        </SidebarScrollableSection>
        <SidebarStickySection
          $hasTopBorder={true}
          css={`
            padding: 8px ${SIDEBAR_PADDING}px;
          `}
        >
          <NavigationItem
            counterEvent="popover_sidebar.settings.click"
            icon={<ConfigureIcon />}
            title="Settings"
            to={Routes.SETTINGS.getUrl({})}
          />
        </SidebarStickySection>
      </SidebarInner>
    </PopoverSidebarContainer>
  );
});

const PopoverSidebarV1NavItems = React.memo(function PopoverSidebarV1NavItems({
  newThreadsListPageEnabled,
  showContextStudio,
}: {
  newThreadsListPageEnabled: boolean;
  showContextStudio: boolean;
}) {
  const agentTasksEnabled = useHexFlag("agent-tasks");
  return (
    <>
      {agentTasksEnabled && (
        <NavigationItem
          counterEvent="popover_sidebar.tasks.click"
          icon={<RecentsIcon />}
          title="Tasks"
          to={Routes.HOME.getUrl({ subView: "tasks" })}
        />
      )}
      <NavigationItem
        counterEvent="popover_sidebar.collections.click"
        icon={<CollectionIcon />}
        title="Collections"
        to={Routes.HOME.getUrl({ subView: "collections" })}
      />
      <LibraryNavItem />
      <Spacer />
      {newThreadsListPageEnabled && (
        <NavigationItem
          counterEvent="popover_sidebar.threads.click"
          icon={<ThreadIcon />}
          title={
            <Flex align="center" gap={6}>
              Threads
              <HexTag intent={Intent.PRIMARY} small={true}>
                Beta
              </HexTag>
            </Flex>
          }
          to={Routes.HOME.getUrl({ subView: "threads" })}
        />
      )}
      <NavigationItem
        counterEvent="popover_sidebar.projects.click"
        icon={<ProjectIcon />}
        title="Projects"
        to={Routes.HOME.getUrl({ subView: "all" })}
      />
      <NavigationItem
        counterEvent="popover_sidebar.components.click"
        icon={<ComponentIcon />}
        title="Components"
        to={Routes.HOME.getUrl({ subView: "components" })}
      />
      <NavigationItem
        counterEvent="popover_sidebar.explore.click"
        icon={<ExploreIcon />}
        title="Explorations"
        to={Routes.HOME.getUrl({ subView: "explorations" })}
      />
      <NavigationItem
        counterEvent="popover_sidebar.data.click"
        icon={<DataSourcesIcon />}
        title="Data"
        to={Routes.HOME.getUrl({ subView: "data" })}
      />
      {showContextStudio && (
        <NavigationItem
          counterEvent="popover_sidebar.context_studio.click"
          icon={<ContextStudioIcon />}
          title="Context Studio"
          to={Routes.CONTEXT_STUDIO.getUrl({})}
        />
      )}
    </>
  );
});

const LibraryNavItem = () => {
  const hasLibraryStatuses = useHasLibraryStatuses();
  return hasLibraryStatuses ? (
    <NavigationItem
      counterEvent="popover_sidebar.library.click"
      icon={<LibraryIcon />}
      title="Library"
      to={Routes.HOME.getUrl({ subView: "library" })}
    />
  ) : null;
};

const StyledHexMenuItem = styled(HexMenuItem)`
  &.${Classes.ACTIVE}, &.${Classes.ACTIVE}:hover {
    background-color: ${({ theme }: { theme: Theme }) => theme.hoverColor};
    color: ${({ theme }: { theme: Theme }) => theme.fontColor.DEFAULT};

    > .${Classes.MENU_ITEM_ICON} .${Classes.ICON} svg {
      color: ${({ theme }: { theme: Theme }) => theme.iconColor};
    }
  }
`;
